import { useState } from "react";
import UploadDocumentDrawer from "@features/vessel-call/vessel-call-status/PortServices/components/UploadDocumentDrawer";
import ViewDetailsSideDrawer from "@features/vessel-call/vessel-call-status/PortServices/components/ViewDetailsSideDrawer";
import CancelServiceDrawer from "@features/vessel-call/vessel-call-status/PortServices/flow/CancelServiceDrawer";
import ChangeStatusServiceDrawer from "@features/vessel-call/vessel-call-status/PortServices/flow/ChangeStatusServiceDrawer";
import { PortServicesFlowProvider } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import UpdateServiceDrawer from "@features/vessel-call/vessel-call-status/PortServices/flow/UpdateServiceTimesDrawer";
import type { PortServicesOutletContext } from "@features/vessel-call/vessel-call-status/PortServices/PortServicesContainer";
import type { MarpolAnnexI } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import type {
  LastTaskDecision,
  LinkedVesselCalls,
  PortService,
  PortServiceDocument,
  PortServiceResourceEquipment,
} from "@services/port-services/types";
import { useOutletContext } from "react-router-dom";

export interface CancelDrawerInfo {
  id: string;
  serviceCategory: string;
  serviceType: string;
  isOpen: boolean;
  ats?: string | Date | null;
  atc?: string | Date | null;
  currentStatus: string;
}

export interface ChangeStatusDrawerInfo {
  id: string;
  isDibbaVesselCall?: boolean;
  serviceCategory: string;
  serviceType: string;
  serviceSubType?: string;
  isOpen: boolean;
  currentStatus: string;
  ats: string | Date | null;
  atc: string | Date | null;
  rts: string | Date | null;
  etc?: string | Date | null;
  ets?: string | Date | null;
  location: string | undefined;
  numberOfSkips: string | number | undefined;
  quantity: string | number | undefined;
  numberOfPackagesHandled?: string | number;
  chargedParty?: string;
  spares?: string | number;
  incidents?: string;
  resourcesAndEquipment?: PortServiceResourceEquipment[];
  internalNotes: string | undefined;
  externalNotes: string | undefined;
  rejectionReason: string | undefined;
  specialInstructions: string | undefined;
  wasteResiduesForDischargeFacility: MarpolAnnexI[] | undefined;
  needsOperationsApproval?: boolean;
  notes: string | undefined;
  serviceReportAttachment?: PortServiceDocument;
  unitOfMeasurement?: string | undefined;
  linkedVesselCalls?: LinkedVesselCalls[];
  documents?: PortServiceDocument[];
}

export interface UploadDocumentDrawerInfo {
  id: string;
  isOpen: boolean;
}

export interface ViewDetailsDrawerInfo {
  id: string;
  isOpen: boolean;
  status: string;
  lastTaskDecisions?: LastTaskDecision[];
}

export interface UpdateDrawerInfo {
  id: string;
  serviceCategory: string;
  serviceType: string;
  isOpen: boolean;
  currentStatus: string;
  ets: string | Date | null;
  etc: string | Date | null;
}

export interface DrawerInjectedProps {
  openCancelDrawer: (row: PortService) => void;
  closeCancelDrawer: () => void;
  openChangeStatusDrawer: (row: PortService) => void;
  closeChangeStatusDrawer: () => void;
  openUploadDocumentDrawer: (drawerInfo: PortService) => void;
  closeUploadDocumentDrawer: () => void;
  openViewDetailsDrawer: (row: PortService) => void;
  closeViewDetailsDrawer: () => void;
  openUpdateDrawer: (row: PortService) => void;
  closeUpdateDrawer: () => void;
}

function withDrawers<P extends object>(
  WrappedComponent: React.ComponentType<P & DrawerInjectedProps>,
) {
  const WithDrawer = (props: P) => {
    const { isDibbaVesselCall = false } = useOutletContext<PortServicesOutletContext>() || {};
    const [cancelDrawerInfo, setCancelDrawerInfo] = useState<CancelDrawerInfo | null>(null);
    const [changeStatusDrawerInfo, setChangeStatusDrawerInfo] =
      useState<ChangeStatusDrawerInfo | null>(null);

    const [uploadDocumentDrawerInfo, setUploadDocumentDrawerInfo] =
      useState<UploadDocumentDrawerInfo | null>(null);
    const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);

    const [viewDetailsDrawerInfo, setViewDetailsDrawerInfo] =
      useState<ViewDetailsDrawerInfo | null>(null);

    const [updateDrawerInfo, setUpdateDrawerInfo] = useState<UpdateDrawerInfo | null>(null);

    const openCancelDrawer = (row: PortService): void => {
      setCancelDrawerInfo({
        id: row.id,
        serviceCategory: row.serviceCategory,
        serviceType: row.serviceType ?? "",
        isOpen: true,
        ats: row.ats ?? null,
        atc: row.atc ?? null,
        currentStatus: row.status,
      });
    };

    const openChangeStatusDrawer = (row: PortService): void => {
      setChangeStatusDrawerInfo({
        id: row.id,
        isDibbaVesselCall,
        currentStatus: row.status,
        serviceCategory: row.serviceCategory,
        serviceType: row.serviceType ?? "",
        serviceSubType: row.serviceSubType ?? "",
        ats: row.ats,
        atc: row.atc,
        rts: row.rts,
        ets: row.ets,
        isOpen: true,
        location: row.location,
        numberOfSkips: row.numberOfSkips,
        quantity: row.quantity,
        numberOfPackagesHandled: row.numberOfPackagesHandled,
        chargedParty: row.chargedParty,
        spares: row.spares,
        incidents: row.incidents,
        resourcesAndEquipment: row.resourcesAndEquipment,
        unitOfMeasurement: row.unitOfMeasurement,
        etc: row.etc,
        internalNotes: row.internalNotes,
        externalNotes: row.externalNotes,
        rejectionReason: row.rejectionReason,
        specialInstructions: row.specialInstructions,
        wasteResiduesForDischargeFacility: row.wasteResiduesForDischargeFacility,
        needsOperationsApproval: row.needsOperationsApproval,
        notes: row.notes,
        serviceReportAttachment: row.serviceReportAttachment,
        linkedVesselCalls: row.linkedVesselCalls,
        documents: row.documents,
      });
    };

    const openUploadDocumentDrawer = (drawerInfo: PortService): void => {
      setUploadDocumentDrawerInfo({
        id: drawerInfo.id,
        isOpen: true,
      });
    };

    const openViewDetailsDrawer = (row: PortService): void => {
      setViewDetailsDrawerInfo({
        id: row.id,
        isOpen: true,
        status: row.status,
        lastTaskDecisions: row.lastTaskDecision ?? [],
      });
    };

    const openUpdateDrawer = (row: PortService): void => {
      setUpdateDrawerInfo({
        id: row.id,
        currentStatus: row.status,
        serviceCategory: row.serviceCategory,
        serviceType: row.serviceType ?? "",
        ets: row.ets ?? null,
        etc: row.etc ?? null,
        isOpen: true,
      });
    };

    const closeCancelDrawer = (): void => setCancelDrawerInfo(null);
    const closeChangeStatusDrawer = (): void => setChangeStatusDrawerInfo(null);
    const closeUploadDocumentDrawer = (): void => setUploadDocumentDrawerInfo(null);
    const closeViewDetailsDrawer = (): void => setViewDetailsDrawerInfo(null);
    const closeUpdateDrawer = (): void => setUpdateDrawerInfo(null);

    return (
      <>
        <WrappedComponent
          {...props}
          shouldRefetch={shouldRefetch}
          openCancelDrawer={openCancelDrawer}
          closeCancelDrawer={closeCancelDrawer}
          openChangeStatusDrawer={openChangeStatusDrawer}
          closeChangeStatusDrawer={closeChangeStatusDrawer}
          openUploadDocumentDrawer={openUploadDocumentDrawer}
          closeUploadDocumentDrawer={closeUploadDocumentDrawer}
          openViewDetailsDrawer={openViewDetailsDrawer}
          closeViewDetailsDrawer={closeViewDetailsDrawer}
          openUpdateDrawer={openUpdateDrawer}
          closeUpdateDrawer={closeUpdateDrawer}
          setShouldRefetch={setShouldRefetch}
        />
        {cancelDrawerInfo?.isOpen && (
          <PortServicesFlowProvider>
            <CancelServiceDrawer
              drawerInfo={cancelDrawerInfo}
              onClose={closeCancelDrawer}
              setShouldRefetch={setShouldRefetch}
            />
          </PortServicesFlowProvider>
        )}
        {changeStatusDrawerInfo?.isOpen && (
          <PortServicesFlowProvider>
            <ChangeStatusServiceDrawer
              drawerInfo={changeStatusDrawerInfo}
              onClose={closeChangeStatusDrawer}
              setShouldRefetch={setShouldRefetch}
            />
          </PortServicesFlowProvider>
        )}
        {uploadDocumentDrawerInfo?.isOpen && (
          <UploadDocumentDrawer
            drawerInfo={uploadDocumentDrawerInfo}
            onClose={closeUploadDocumentDrawer}
            setShouldRefetch={setShouldRefetch}
          />
        )}
        {viewDetailsDrawerInfo?.isOpen && (
          <ViewDetailsSideDrawer
            isOpen={viewDetailsDrawerInfo.isOpen}
            onClose={closeViewDetailsDrawer}
            lastTaskDecisions={viewDetailsDrawerInfo?.lastTaskDecisions}
          />
        )}
        {updateDrawerInfo?.isOpen && (
          <UpdateServiceDrawer
            drawerInfo={updateDrawerInfo}
            onClose={closeUpdateDrawer}
            setShouldRefetch={setShouldRefetch}
          />
        )}
      </>
    );
  };

  return WithDrawer;
}

export default withDrawers;
